/**
 * WhatsApp utilities for building WhatsApp Web/app links
 * 
 * Uses the wa.me URL scheme which works on:
 * - Desktop: Opens WhatsApp Web
 * - Mobile: Opens WhatsApp app
 * 
 * Note: This does NOT use the WhatsApp Business API.
 * The user must manually press "Send" after the link opens.
 */

// =============================================================================
// Types
// =============================================================================

export interface BuildWhatsAppLinkOptions {
  /** Phone number (can include +, spaces, dashes - will be sanitized) */
  phone: string;
  /** Message to prefill (will be URL encoded) */
  message: string;
}

// =============================================================================
// Helper: Normalize phone number for WhatsApp
// =============================================================================

/**
 * Normalizes a phone number to international format for WhatsApp
 * 
 * Handles common Dutch phone formats:
 * - 06 12345678 → 31612345678
 * - +31 6 12345678 → 31612345678
 * - 0031612345678 → 31612345678
 * - 31612345678 → 31612345678 (already correct)
 * 
 * @param phone - Raw phone number
 * @returns Normalized phone number (digits only, with country code)
 */
export function normalizePhoneForWhatsApp(phone: string): string {
  // Strip everything except digits
  const digits = phone.replace(/[^0-9]/g, '');
  
  // Handle Dutch numbers starting with 06 (mobile)
  if (digits.startsWith('06') && digits.length === 10) {
    // 0612345678 → 31612345678
    return '31' + digits.slice(1);
  }
  
  // Handle Dutch numbers starting with 0 (landline or mobile)
  if (digits.startsWith('0') && digits.length === 10) {
    // 0201234567 → 31201234567
    return '31' + digits.slice(1);
  }
  
  // Handle 0031 prefix (international format without +)
  if (digits.startsWith('0031')) {
    // 0031612345678 → 31612345678
    return digits.slice(2);
  }
  
  // Handle 00 prefix for other countries
  if (digits.startsWith('00') && digits.length >= 12) {
    // 0049123456789 → 49123456789
    return digits.slice(2);
  }
  
  // Already in international format or unknown format
  return digits;
}

// =============================================================================
// Helper: Build WhatsApp link
// =============================================================================

/**
 * Builds a WhatsApp Web/app link with prefilled message
 * 
 * @example
 * ```ts
 * const url = buildWhatsAppLink({
 *   phone: '+31 6 12345678',
 *   message: 'Beste klant,\n\nHierbij uw factuur.',
 * });
 * // Returns: https://wa.me/31612345678?text=Beste%20klant%2C%0A%0AHierbij%20uw%20factuur.
 * 
 * // Also handles Dutch local format:
 * const url2 = buildWhatsAppLink({
 *   phone: '06 12345678',
 *   message: 'Hallo!',
 * });
 * // Returns: https://wa.me/31612345678?text=Hallo!
 * ```
 * 
 * @param options - Phone number and message
 * @returns WhatsApp URL string
 */
export function buildWhatsAppLink({ phone, message }: BuildWhatsAppLinkOptions): string {
  // Normalize phone to international format
  const normalizedPhone = normalizePhoneForWhatsApp(phone);
  
  // URL encode the message
  const encodedMessage = encodeURIComponent(message);
  
  return `https://wa.me/${normalizedPhone}?text=${encodedMessage}`;
}

// =============================================================================
// Helper: Validate phone number for WhatsApp
// =============================================================================

/**
 * Checks if a phone number is valid for WhatsApp
 * 
 * Requirements:
 * - Must have at least 10 digits (after normalization)
 * - After normalization, must have a valid country code
 * 
 * @param phone - Phone number to validate
 * @returns true if valid for WhatsApp
 */
export function isValidWhatsAppPhone(phone: string | null | undefined): boolean {
  if (!phone) return false;
  
  // Normalize the phone number first
  const normalized = normalizePhoneForWhatsApp(phone);
  
  // Must have at least 10 digits after normalization
  // This ensures we have country code + number
  // e.g., 31612345678 (11 digits for Dutch mobile)
  if (normalized.length < 10) return false;
  
  // Check for common valid country codes
  // Netherlands (31), Belgium (32), Germany (49), UK (44), France (33), etc.
  const validCountryCodes = ['31', '32', '33', '34', '39', '44', '49', '1'];
  const hasValidCountryCode = validCountryCodes.some(code => normalized.startsWith(code));
  
  // If it starts with a known country code, it's valid
  // Otherwise, accept if it has enough digits (for unknown countries)
  return hasValidCountryCode || normalized.length >= 11;
}

// =============================================================================
// Helper: Format phone for display
// =============================================================================

/**
 * Formats a phone number for display in the UI
 * 
 * @param phone - Raw phone number
 * @returns Formatted phone number or original if can't format
 */
export function formatPhoneForDisplay(phone: string): string {
  const sanitized = phone.replace(/[^0-9+]/g, '');
  
  // Dutch mobile: +31 6 1234 5678
  if (sanitized.startsWith('+316') || sanitized.startsWith('316')) {
    const digits = sanitized.replace(/^\+/, '');
    if (digits.length === 11) {
      return `+${digits.slice(0, 2)} ${digits.slice(2, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
    }
  }
  
  // Dutch landline: +31 20 123 4567
  if (sanitized.startsWith('+31') || sanitized.startsWith('31')) {
    const digits = sanitized.replace(/^\+/, '');
    if (digits.length === 11) {
      return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
  }
  
  // Return original if can't format
  return phone;
}
